"use client"

import React, { useRef, useEffect, useState } from "react";
import { cn } from '@sourcepilot/ui/framework/data/utils';

interface MiniAppTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  containerWidth: number;
  isActive?: boolean;
  isHovered?: boolean;
}

export function MiniAppText({
  children,
  className,
  containerWidth,
  isActive = false,
  isHovered = false,
  ...props
}: MiniAppTextProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [fadeStartPx, setFadeStartPx] = useState(containerWidth);

  useEffect(() => {
    const updateTruncation = () => {
      if (textRef.current) {
        const { scrollWidth, offsetWidth } = textRef.current;

        // Check if the text is truncated
        if (scrollWidth > offsetWidth) {
          setIsTruncated(true);

          // Measure the width of the last 3 visible characters
          const visibleWidth = offsetWidth;
          const range = document.createRange();
          const textNode = textRef.current.firstChild as Text;

          if (textNode) {
            setFadeStartPx(Math.max(0, offsetWidth * 0.8)); // Ensure it doesn't go negative
          }
        } else {
          setIsTruncated(false);
        }
      }
    };

    updateTruncation();
  }, [children, containerWidth]);

  return (
    <div
      className={cn(
        "overflow-hidden relative py-[6px]",
        isActive && "text-[#00ccaa]",
        isHovered && "underline",
        className
      )}
      {...props}
    >
      <span
        ref={textRef}
        className="whitespace-nowrap inline-block align-top hover:underline"
        style={{
          maskImage: isTruncated
            ? `linear-gradient(to left, transparent ${fadeStartPx}px, black)`
            : "none",
          WebkitMaskImage: isTruncated
            ? `linear-gradient(to left, transparent ${fadeStartPx}px, black)`
            : "none",
        }}
      >
        {children}
      </span>
    </div>
  );
}

